import * as React from 'react'
import { useDispatch } from 'react-redux'
import { setIsStored } from '../../../redux/slices/Alerts'
import { requestTreeHardDelete, confirmTreeHardDelete } from '../../../services'
import { invalidateMapData } from '../../../redux/slices/Tree/treeFeatures'
import { clearFilteredData, setEnabledFilter } from '../../../redux/slices/Search/input'
import VerificationCodeDialog from '../../VerificationCodeDialog'
import {
  setPendingDelete,
  getPendingDelete,
  clearPendingDelete,
} from '../../../utils/pendingDelete'

const PENDING_TABLE = 'tree'

/**
 * Modal de eliminación con OTP a nivel de tabla. Se controla de forma imperativa
 * (`ref.openModal(tree)`) para que el flujo sobreviva a refetch/re-render o a la
 * virtualización del DataGrid mientras el usuario va a su correo a leer el código.
 * Además persiste el borrado en curso para reabrirse tras una recarga de página.
 */
const DeleteTreeModal = React.forwardRef((_props, ref) => {
  const dispatch = useDispatch()
  const [tree, setTree] = React.useState(null)
  const [otpDialogOpen, setOtpDialogOpen] = React.useState(false)
  const [otpLoading, setOtpLoading] = React.useState(false)
  const [otpError, setOtpError] = React.useState(null)
  const [resendCount, setResendCount] = React.useState(0)
  const [retryAfterSeconds, setRetryAfterSeconds] = React.useState(null)

  const requestDeletion = React.useCallback(
    async targetTree => {
      const result = await requestTreeHardDelete(targetTree.uuid)

      if (result?.cooldownActive) {
        setRetryAfterSeconds(result.retryAfter ?? null)
        setOtpDialogOpen(true)
        setPendingDelete({ table: PENDING_TABLE, id: targetTree.uuid, label: targetTree.codigo })
      } else if (result?.requested) {
        setRetryAfterSeconds(null)
        setResendCount(c => c + 1)
        setOtpDialogOpen(true)
        setPendingDelete({ table: PENDING_TABLE, id: targetTree.uuid, label: targetTree.codigo })
      } else {
        dispatch(
          setIsStored({
            stored: false,
            message: result?.message || 'Error al solicitar eliminación.',
          }),
        )
      }
    },
    [dispatch],
  )

  React.useImperativeHandle(ref, () => ({
    openModal: targetTree => {
      if (!targetTree?.uuid) return
      setTree(targetTree)
      setOtpError(null)
      requestDeletion(targetTree)
    },
  }))

  // Restaurar el modal tras una recarga: el código ya está en el correo, así que
  // se reabre el OTP sin volver a solicitarlo.
  React.useEffect(() => {
    const pending = getPendingDelete(PENDING_TABLE)
    if (pending) {
      setTree({ uuid: pending.id, codigo: pending.label })
      setOtpDialogOpen(true)
    }
  }, [])

  const handleRequestNewCode = async () => {
    if (!tree) return
    const result = await requestTreeHardDelete(tree.uuid)
    if (result?.cooldownActive) {
      setRetryAfterSeconds(result.retryAfter ?? null)
      setOtpError('Espera antes de reenviar.')
    } else if (result?.requested) {
      setRetryAfterSeconds(null)
      setResendCount(c => c + 1)
      setOtpError(null)
    }
  }

  const handleOtpConfirm = async code => {
    if (!tree) return
    setOtpLoading(true)
    setOtpError(null)
    const result = await confirmTreeHardDelete(tree.uuid, code)
    setOtpLoading(false)

    if (result?.deleted) {
      setOtpDialogOpen(false)
      clearPendingDelete()
      dispatch(setIsStored({ stored: true, message: 'Árbol eliminado permanentemente.' }))
      dispatch(setEnabledFilter(false))
      dispatch(clearFilteredData())
      dispatch(invalidateMapData())
    } else {
      setOtpError(result?.message || 'Código inválido o expirado.')
    }
  }

  const handleOtpClose = () => {
    if (otpLoading) return
    setOtpDialogOpen(false)
    setOtpError(null)
    clearPendingDelete()
  }

  return (
    <VerificationCodeDialog
      open={otpDialogOpen}
      onClose={handleOtpClose}
      onConfirm={handleOtpConfirm}
      onRequestNew={handleRequestNewCode}
      loading={otpLoading}
      resourceLabel={tree?.codigo || tree?.uuid || ''}
      error={otpError}
      resendCount={resendCount}
      retryAfterSeconds={retryAfterSeconds}
    />
  )
})

DeleteTreeModal.displayName = 'DeleteTreeModal'

export default DeleteTreeModal
